<template>
    <div>
        <div>
            <h3 class="mb-4 ml-4 d-flex align-items-center">
                <span id="poolLabel" class="flex-grow-1">{{pool.name}}</span>
                <w-button :to="{name: 'Lab', params: { id: pool.waterBodyId }}" class="mr-3 px-4">Lab</w-button>
                <router-link :to="{name: 'Questions', params: { id: pool.waterBodyId }}">
                    <w-icon id="editPool" class="float-right cursor-pointer" name="gear"/>
                </router-link>
            </h3>
            <div class="w-alert" v-if="pool.waterBodyId">
                <template v-if="pool.alerts && pool.alerts.length">WaterGuru Alerts <i class="fa fa-warning"></i>
                </template>
                <template v-else>Status OK</template>
            </div>
            <div class="alerts" v-if="pool && pool.status === 'RED'">
                <ul>
                    <li class="mt-3" v-for="alert in pool.alerts" :key="alert.text">
                        <i class="fa fa-circle pl-0" :style="{color:alert.color}"></i>{{alert.text}}
                        <w-icon class="float-right cursor-pointer" alt="view alert" height="16px" name="chevron"/>
                    </li>
                </ul>
            </div>
            <div class="dashboard-item" v-if="pool.pods && pool.pods[0].refillables">
                <status class="dashboard-item-inner"/>
            </div>
            <div class="dashboard-item" v-if="pool.measurements">
                <conditions class="dashboard-item-inner"/>
            </div>
            <div class="dashboard-item readings" v-if="tuned">
                <div class="dashboard-item-inner">
                    <div class="title-box">
                        <div class="title-left title-28">
                            <span>Pool Readings</span>
                        </div>
                        <div class="title-right d-flex align-content-center">
                            <template v-if="pool.measurements && pool.measurements.length">
                                <div class="form-check-inline" v-for="(measurement, key) in chartData" :key="measurement.type">
                                    <input type="radio" class="form-check-input" :id="'radio'+ key" :checked="measurementType === key"
                                           @change="measurementType = key" name="optradio" title="Type">
                                    <label class="form-check-label" :for="'radio'+ key">{{ key }}</label>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="item-box mt-3">
                        <div v-if="chartData">
                            <vue-charts :height="200"
                                        :readingdata="chartData"
                                        :measurementtype="measurementType"
                                        :pooltitle="pool.name"
                                        :weektempdata="waterTempData"
                                        :week-data="weekArr(true)">
                            </vue-charts>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <w-version/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { moment } from '../../plugins'
import VueCharts from './Chart'
import Conditions from './Conditions'
import Status from './Status'

export default {
    name: 'Pool',
    data () {
        return {
            measurementType: ''
        }
    },
    components: {
        Status,
        Conditions,
        VueCharts
    },
    watch: {
        chartData: {
            immediate: true,
            handler (value) {
                this.measurementType = Object.keys(value)[0]
            }
        }
    },
    computed: {
        ...mapGetters({
            pool: 'pools/pool',
            history: 'pools/history'
        }),
        chartData () {
            const format = (type, value) => {
                if (type === 'FREE_CL') {
                    return value > 10 ? 10 : value
                } else if (type === 'PH') {
                    return value > 8.5 ? 8.5 : (value < 6.5 ? 6.5 : value)
                } else {
                    return value
                }
            }
            const week = this.weekArr()
            let obj = {
                'Free cl': week.map((item, index) => ({ x: index, y: null })),
                'pH': week.map((item, index) => ({ x: index, y: null })),
                'Water Temp': week.map((item, index) => ({ x: index, y: null }))
            }

            Object.keys(this.tempArr()).forEach(key => {
                const temps = this.tempArr()[key]
                const index = week.indexOf(key)
                if (index !== -1) {
                    const freeClArr = temps['FREE_CL'].filter(item => item || item === 0).map(item => ({ x: index, y: format('FREE_CL', item) }))
                    obj['Free cl'].splice(obj['Free cl'].findIndex(item => item.x === index), 1, ...freeClArr)

                    const phArr = temps['PH'].filter(item => item || item === 0).map(item => ({ x: index, y: format('PH', item) }))
                    obj['pH'].splice(obj['pH'].findIndex(item => item.x === index), 1, ...phArr)

                    const wTempArr = temps['waterTemp'].filter(item => item || item === 0).map(item => ({ x: index, y: format('waterTemp', item) }))
                    obj['Water Temp'].splice(obj['Water Temp'].findIndex(item => item.x === index), 1, ...wTempArr)
                }
            })

            return obj
        },
        waterTempData () {
            return this.weekArr().map(item => this.tempArr()[item] ? this.tempArr()[item]['waterTemp'] : 0)
        },
        tuned () {
            return !this.pool.alerts || !this.pool.alerts.length || !this.pool.alerts.find(item => item.condition === 'NO_POD')
        }
    },
    methods: {
        weekArr (day) {
            return Array.from(moment.range(moment().subtract(6, 'd'), moment()).by('d')).reduce((acc, item) => {
                day ? acc.push(item.format('ddd')) : acc.push(item.format('YYYY-MM-DD'))
                return acc
            }, [])
        },

        tempArr () {
            return this.history.reduce((acc, item) => {
                if (this.pool.pods && this.pool.pods[0] && this.pool.pods[0].podId && item.podId === this.pool.pods[0].podId) {
                    const key = moment.utc(item.time).local().format('YYYY-MM-DD')
                    acc[key] = {
                        time: [...(acc[key] && acc[key].time ? acc[key].time : []), item.time],
                        waterTemp: [...(acc[key] && acc[key].waterTemp ? acc[key].waterTemp : []), item.waterTempFahr],
                        FREE_CL: [...(acc[key] && acc[key].FREE_CL ? acc[key].FREE_CL : []), item.freeCl],
                        PH: [...(acc[key] && acc[key].PH ? acc[key] && acc[key].PH : []), item.ph]
                    }
                }
                return acc
            }, [])
        }
    }
}
</script>

<style scoped lang="scss">
    .dashboard {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        h3 {
            padding-left: 15px;
            max-width: 754px;
        }

        .w-alert {
            font-size: 37px;
            display: inline-block;
            height: 55px;
            line-height: 55px;
            background-color: #23B8DC;
            color: #FFF;
            padding-left: 40px;
            padding-right: 40px;
            border-radius: 29px;
        }

        .alerts {
            padding-left: 24px;
            padding-right: 40px;
            max-width: 784px;

            .fa-ellipsis-v {
                color: #C4CFD5;
            }

            ul {
                padding-left: calc(75px - 1.5rem);
                list-style-type: none;
            }
        }

        .dashboard-item {
            background-color: #FFF;
            border-radius: 12px;
            max-width: 784px;
            margin-top: 20px;
            padding: 22px 40px;

            .title-box {
                .title-left {
                    width: 70%;
                }

                .title-left.title-28 {
                    font-size: 28px;
                    font-weight: 300;
                }

                .title-right {
                    width: 30%;
                }
            }

            .item-box {
                margin-left: 30px;
            }
        }

        .version {
            max-width: 784px;
        }

    }

    .dashboard-item.readings {
        .title-box {
            display: flex;
            justify-content: space-between;

            .title-left, .title-right {
                width: unset !important;
            }
        }
    }

    @media (max-width: 1024px) {
        .dashboard {
            padding-left: 20px;

            .alerts,
            .version {
                width: 100%;
            }

            .alerts {
                padding-left: 16px;
                padding-right: 10px;
            }

            .dashboard-item {
                padding-left: 16px;
                padding-right: 10px;

                .item-box {
                    margin-left: 0;
                }
            }
        }
    }

    @media (max-width: 768px) {
        .dashboard {
            h3 {
                width: 100%;
                margin-left: 0 !important;
                padding-right: 15px;
            }

            .w-alert {
                font-size: 24px;
            }

            .alerts {
                margin-left: 0 !important;

                ul {
                    padding-right: 10px;
                    padding-left: 16px;
                }
            }

            .dashboard-item {

                .title-box {
                    .title-left {
                        span {
                            font-size: 22px;
                            font-weight: 600;
                        }
                    }

                    .title-right {
                        h4 {
                            font-size: 20px;
                        }
                    }
                }

                .dashboard-item-inner {
                    width: 100%;
                }
            }

            .dashboard-item.readings {
                .title-box {
                    flex-direction: column;

                    .title-right {
                        font-size: 12px;
                    }
                }
            }
        }
    }
</style>
